import React, { useState, useEffect } from 'react';
import { Image } from '../data/apartmentData';
import { getApartmentImages } from '../services/imageService';

interface ImageStackProps {
  images: Image[];
  apartmentId: string;
  className?: string;
  onLightboxOpen?: (images: Image[], apartmentId: string) => void;
}

export const ImageStack: React.FC<ImageStackProps> = ({ 
  images, 
  apartmentId, 
  className = '',
  onLightboxOpen
}) => {
  const [displayImages, setDisplayImages] = useState<Image[]>(images);
  const [isLoading, setIsLoading] = useState(false);

  // Load images when component mounts or apartmentId changes
  useEffect(() => {
    const loadImages = async () => {
      setIsLoading(true);
      try {
        const apartmentImages = await getApartmentImages(apartmentId, images);
        setDisplayImages(apartmentImages);
      } catch (error) {
        console.error('Failed to load images:', error);
        // Fallback to provided images or empty array
        setDisplayImages(images);
      } finally {
        setIsLoading(false);
      }
    };

    loadImages();
  }, [apartmentId, images]);
  
  // Show first 3 images in the stack
  const stackImages = displayImages.slice(0, 3);

  const handleStackClick = () => {
    if (onLightboxOpen) {
      onLightboxOpen(displayImages, apartmentId);
    }
  };

  return (
    <div 
      className={`image-stack ${className}`}
      onClick={handleStackClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleStackClick();
        }
      }}
      aria-label={`View ${displayImages.length} images for apartment ${apartmentId}`}
    >
      {isLoading ? (
        // Loading placeholder
        <div className="image-stack-loading">
          <div className="image-stack-spinner"></div>
        </div>
      ) : (
        stackImages.map((img, index) => (
          <img 
            key={index} 
            src={img.url} 
            alt={img.alt} 
            style={{ 
              zIndex: stackImages.length - index, 
              marginLeft: `${index * 10}px` 
            }}
            loading="lazy"
          />
        ))
      )}
      
      {/* Image count indicator */}
      {displayImages.length > 3 && (
        <div className="image-count-indicator">
          +{displayImages.length - 3}
        </div>
      )}
    </div>
  );
};

 